import { FormEvent, useEffect, useState } from 'react';

import { IconBuilding, IconKey, IconMail, IconUser } from '@tabler/icons-react';

import { Link, createFileRoute, useRouter, useSearch } from '@tanstack/react-router';

import {
  Alert,
  Anchor,
  Button,
  Checkbox,
  Container,
  PasswordInput,
  Stack,
  Text,
  TextInput,
  Title,
} from '@optiro/ui';

import { useClaimPartner, usePartnerClaimInfo } from '@/hooks/usePartners/usePartners';
import { track } from '@/modules/Analytics';
import { useAuth } from '@/providers/AuthProvider/AuthProvider';

export const Route = createFileRoute('/experiences/claim-partner')({
  component: ClaimPartnerPage,
  validateSearch: (search: Record<string, unknown>) => ({
    token: typeof search.token === 'string' ? search.token : '',
  }),
});

export function ClaimPartnerPage() {
  const { token } = useSearch({ from: '/experiences/claim-partner' });
  const router = useRouter();
  const { isSignedIn, loading, login } = useAuth();
  const mutation = useClaimPartner();
  const { data: claimInfo } = usePartnerClaimInfo(token);

  useEffect(() => {
    if (!loading && isSignedIn && mutation.isIdle) {
      router.navigate({ to: '/app/partners/link', search: { token, force: false } });
    }
  }, [isSignedIn, loading, mutation.isIdle, router, token]);

  const [companyName, setCompanyName] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [privacyAccepted, setPrivacyAccepted] = useState(false);

  useEffect(() => {
    if (!claimInfo) return;
    setCompanyName((current) => current || claimInfo.company_name);
    setEmail((current) => current || claimInfo.email);
  }, [claimInfo]);

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    mutation.mutate(
      {
        token,
        company_name: companyName,
        name,
        email,
        password,
        terms_accepted: termsAccepted,
        privacy_accepted: privacyAccepted,
      },
      {
        onSuccess: async () => {
          track('signup_completed', { origin: 'ue_invite' });
          await login(email, password);
          router.navigate({ to: '/app/dashboard' });
        },
      }
    );
  };

  return (
    <Container size="sm" py="xl">
      <form onSubmit={handleSubmit}>
        <Stack gap="md" maw={400} mx="auto">
          <Title order={2} ta="center">
            Opret din virksomhed
          </Title>
          <Text ta="center" c="dimmed" size="sm">
            Du er inviteret til at samarbejde på Optiro. Opret din virksomhedskonto for at komme i
            gang.
          </Text>

          <TextInput
            label="Virksomhedsnavn"
            required
            size="lg"
            value={companyName}
            onChange={(e) => setCompanyName(e.currentTarget.value)}
            leftSection={<IconBuilding size={18} />}
            placeholder="Acme ApS"
            disabled={mutation.isPending}
          />

          <TextInput
            label="Dit navn"
            required
            size="lg"
            value={name}
            onChange={(e) => setName(e.currentTarget.value)}
            leftSection={<IconUser size={18} />}
            placeholder="Anders Jensen"
            disabled={mutation.isPending}
          />

          <TextInput
            label="Email"
            type="email"
            required
            size="lg"
            value={email}
            onChange={(e) => setEmail(e.currentTarget.value)}
            leftSection={<IconMail size={18} />}
            placeholder="dig@virksomhed.dk"
            disabled={mutation.isPending}
          />

          <PasswordInput
            label="Vælg kodeord"
            required
            size="lg"
            value={password}
            onChange={(e) => setPassword(e.currentTarget.value)}
            leftSection={<IconKey size={18} />}
            placeholder="Vælg et kodeord"
            disabled={mutation.isPending}
          />

          <Checkbox
            required
            checked={termsAccepted}
            onChange={(e) => setTermsAccepted(e.currentTarget.checked)}
            label={
              <Text size="sm">
                Jeg accepterer Optiros{' '}
                <Anchor
                  href={import.meta.env.VITE_LANDING_PAGE_URL + '/terms-and-conditions'}
                  target="_blank"
                  size="sm"
                >
                  vilkår og betingelser
                </Anchor>
              </Text>
            }
          />
          <Checkbox
            required
            checked={privacyAccepted}
            onChange={(e) => setPrivacyAccepted(e.currentTarget.checked)}
            label={
              <Text size="sm">
                Jeg accepterer Optiros{' '}
                <Anchor
                  href={import.meta.env.VITE_LANDING_PAGE_URL + '/privacy-policy'}
                  target="_blank"
                  size="sm"
                >
                  privatlivspolitik
                </Anchor>
              </Text>
            }
          />

          <Button type="submit" size="lg" fullWidth loading={mutation.isPending}>
            Opret konto og accepter invitation
          </Button>

          {mutation.isError && (
            <Alert color="red">
              {(mutation.error as { response?: { data?: { error?: string } } })?.response?.data
                ?.error ?? 'Noget gik galt'}
            </Alert>
          )}

          <Text ta="center" size="sm">
            Har du allerede en konto?{' '}
            <Link to="/experiences/login" className="text-blue-600 hover:underline">
              Log ind og link i stedet
            </Link>
          </Text>
        </Stack>
      </form>
    </Container>
  );
}
